import { EntityDrawer } from "@/components/cinch/EntityDrawer";
import { Badge } from "@/components/ui/badge";

import type { OpenDeal, PipelineBannerData } from "../api";
import { hubspotDealUrl } from "../constants";
import { fmtCents, fmtCentsWhole, fmtCount } from "../format";

/**
 * Drill-down for the Summary banner's pipeline tiles.
 *
 * One drawer serves a whole row of three tiles — total pipeline, open deals and
 * avg deal size all describe the same deal set — so the header carries all three
 * numbers and whichever tile was clicked, that figure is present. `stage` narrows
 * the same list when a stage tile was the entry point.
 *
 * The stage breakdown in the header covers EVERY stage, not just the three the
 * banner tiles show: on the Sales Pipeline the tiles omit Demo Scheduled and
 * Verbal Commit, so the tiles deliberately don't sum to the banner total and a
 * reader needs to see where the remainder sits.
 */
export function PipelineTileDrawer({
  open,
  expanded,
  onOpenChange,
  onExpandedChange,
  onClose,
  title,
  banner,
  stage,
  onStageChange,
}: {
  open: boolean;
  expanded: boolean;
  onOpenChange: (v: boolean) => void;
  onExpandedChange: (v: boolean) => void;
  onClose: () => void;
  title: string;
  banner: PipelineBannerData | undefined;
  /** When set, only this stage's deals are listed. */
  stage?: string;
  /** Lets the header's stage chips re-scope the drawer in place. */
  onStageChange?: (stage: string | undefined) => void;
}) {
  const all = banner?.deals ?? [];
  const rows = stage ? all.filter((d) => d.stage_name === stage) : all;
  const shown = expanded ? rows : rows.slice(0, 12);
  const rollup = banner?.stage_rollup ?? [];

  // Scoped totals so the header always describes what's listed below it —
  // clicking into one stage must not keep showing the whole pipeline's number.
  const scopedCents = rows.reduce((a, d) => a + (d.amount_cents ?? 0), 0);
  const scopedAvg = rows.length > 0 ? Math.round(scopedCents / rows.length) : 0;
  const amountless = rows.length > 0 && rows.every((d) => !d.amount_cents);

  return (
    <EntityDrawer
      open={open}
      onOpenChange={onOpenChange}
      expanded={expanded}
      onExpandedChange={onExpandedChange}
      onClose={onClose}
      title={title}
      subtitle={stage ? `${stage} · open deals` : "Open deals"}
    >
      <div className="border-b border-border p-4">
        <div className="grid grid-cols-3 gap-3">
          <Metric label="Open pipeline" value={fmtCentsWhole(scopedCents)} />
          <Metric label="Open deals" value={fmtCount(rows.length)} />
          <Metric label="Avg deal" value={fmtCentsWhole(scopedAvg)} />
        </div>
        {amountless && (
          <p className="mt-2 text-[10px] text-muted-foreground">
            This pipeline doesn't track deal amounts in HubSpot, so the dollar
            figures read $0 by design — the deal count is the signal here.
          </p>
        )}
        {rollup.length > 0 && (
          <div className="mt-3">
            <div className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground/70">
              By stage {stage ? "— click to clear" : "— click to narrow"}
            </div>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {rollup.map((s) => {
                const active = s.stage_name === stage;
                return (
                  <button
                    key={s.stage_name}
                    type="button"
                    onClick={() =>
                      onStageChange?.(active ? undefined : s.stage_name)
                    }
                    className={`rounded-full border px-2 py-0.5 text-[11px] transition ${
                      active
                        ? "border-foreground bg-muted font-semibold"
                        : "border-border hover:bg-muted/60"
                    }`}
                  >
                    {s.stage_name}{" "}
                    <span className="tabular-nums text-muted-foreground">
                      {fmtCount(s.deal_count)} · {fmtCents(s.amount_cents)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="p-4">
        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          {expanded ? `All deals (${rows.length})` : "Largest deals"}
        </div>
        {rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            No open deals{stage ? ` in ${stage}` : ""}.
          </p>
        ) : (
          <table className="mt-2 w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-muted-foreground">
              <tr className="border-b border-border">
                <th className="px-2 py-1.5 text-left font-semibold">Deal</th>
                {!stage && (
                  <th className="px-2 py-1.5 text-left font-semibold">Stage</th>
                )}
                {expanded && (
                  <th className="px-2 py-1.5 text-left font-semibold">Owner</th>
                )}
                <th className="px-2 py-1.5 text-left font-semibold">Closes</th>
                <th className="px-2 py-1.5 text-right font-semibold">Amount</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((d) => (
                <DealRow
                  key={d.hubspot_deal_id}
                  deal={d}
                  showStage={!stage}
                  showOwner={expanded}
                />
              ))}
            </tbody>
          </table>
        )}
        {!expanded && rows.length > shown.length && (
          <p className="mt-2 text-[10px] text-muted-foreground">
            {rows.length - shown.length} more — expand to see all.
          </p>
        )}
      </div>
    </EntityDrawer>
  );
}

function DealRow({
  deal,
  showStage,
  showOwner,
}: {
  deal: OpenDeal;
  showStage: boolean;
  showOwner: boolean;
}) {
  const overdue = isPastDate(deal.close_date);
  return (
    <tr className="border-b border-border/60">
      <td className="px-2 py-1.5">
        <a
          href={hubspotDealUrl(deal.hubspot_deal_id)}
          target="_blank"
          rel="noreferrer noopener"
          className="hover:text-power hover:underline"
        >
          {deal.name}
        </a>
      </td>
      {showStage && (
        <td className="px-2 py-1.5">
          <Badge variant="secondary">{deal.stage_name}</Badge>
        </td>
      )}
      {showOwner && (
        <td className="px-2 py-1.5 text-xs text-muted-foreground">
          {deal.owner_display_name ?? "—"}
        </td>
      )}
      <td
        className={`px-2 py-1.5 text-xs tabular-nums ${
          overdue ? "font-semibold text-danger" : "text-muted-foreground"
        }`}
      >
        {deal.close_date ? fmtShortDate(deal.close_date) : "—"}
        {overdue && " (overdue)"}
      </td>
      <td className="px-2 py-1.5 text-right font-semibold tabular-nums">
        {deal.amount_cents ? fmtCentsWhole(deal.amount_cents) : "—"}
      </td>
    </tr>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/60">
        {label}
      </div>
      <div className="mt-0.5 text-lg font-bold tabular-nums">{value}</div>
    </div>
  );
}

/** Parse a date-only ISO string in LOCAL time — `new Date("2026-09-17")` is UTC
 *  midnight and renders as the previous day west of UTC. */
function parseIsoDate(iso: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

function fmtShortDate(iso: string): string {
  const d = parseIsoDate(iso);
  return d
    ? d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : iso;
}

function isPastDate(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const d = parseIsoDate(iso);
  if (d == null) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d.getTime() < today.getTime();
}
